import { cn } from '@/lib/utils.ts'
import PropertyPopoverTrigger from '@/components/common/propertyPopoverOverlay/PropertyPopoverTrigger.tsx'
import { useTranslation } from 'react-i18next'
import PropertyPopover from '@/components/common/propertyPopoverOverlay/PropertyPopover.tsx'
import NavStagesTriggerLabel from '@/components/common/menu/navOverlays/navStagesOverlay/NavStagesTriggerLabel.tsx'
import useNavStagesData from '@/components/common/menu/lib/hooks/useNavStagesData.ts'

type PropsT = {
  className?: string
  /**
   * Extra classes merged onto the trigger Button (via PropertyPopoverTrigger.cl).
   * Use this to flip justify-start → justify-end in the topbar context where
   * the switcher sits at the right edge and the label should hug the
   * trailing chevron rather than the leading project icon.
   */
  triggerClassName?: string
  /**
   * Popover alignment relative to the full-width trigger. `'start'` anchors
   * the popover at the trigger's left edge (desktop sidebar default).
   * `'end'` anchors it at the right edge — needed in the mobile topbar
   * where the trigger spans the whole bar but the chevron lives on the
   * right: anchoring at `'start'` there would drop the popover all the way
   * to the left of the screen (bug: options appear detached from the
   * chevron the user just tapped).
   */
  popoverAlign?: 'start' | 'center' | 'end'
}

export default function NavStagesPopover({
  className,
  triggerClassName,
  popoverAlign,
}: PropsT) {
  const { t } = useTranslation()

  const { stagesProperties, selected, onSelect, stageId } = useNavStagesData()

  if (stagesProperties.length === 0) return <></>

  // Use PropertyPopover directly (not PopoverOverlay) so the stage options
  // appear anchored to the trigger on ALL screen sizes. PopoverOverlay on
  // mobile would render as a bottom-sheet — disorienting when the trigger
  // lives at the top of the drawer.
  return (
    // Padding mirrors NavigationGroupsGroup (`md:px-4`) + a small `py-2` so the
    // switcher sits in its own row instead of bumping into the logo separator
    // above and the first nav group's border below.
    <header className={cn(`w-full px-2 py-2 md:px-4`, className)}>
      <PropertyPopover
        align={popoverAlign}
        className={`md:max-w-[calc(var(--sidebar-width)-48px)]`}
        properties={stagesProperties}
        fallbackMessage={t(`navigation.singleProjectWarning`)}
        addSelectedValue={onSelect}
        closeOnSelect={true}
        selectedValues={[stageId]}
        popoverHeader={t('navigation.currentStageLabel')}
        customTrigger={
          // `justify-start` + `px-2` make the trigger's icon land at the same
          // x-position as NavigationGroupItemContent (which has its own
          // `px-2`), so the stage icon column lines up with the nav-item
          // icon column below.
          <PropertyPopoverTrigger
            disabled={stagesProperties.length === 0}
            variant={`ghost`}
            // Button's ghost variant uses `hover:bg-zinc-100` which is
            // virtually invisible against the sidebar background. Force
            // `hover:bg-accent` to match NavigationGroupItemContent below
            // so the switcher reads as a tappable row, not flat text.
            cl={cn(
              `h-full w-full justify-start rounded-md px-2 hover:bg-accent`,
              triggerClassName,
            )}
            label={<NavStagesTriggerLabel /> }
            icon={selected?.icon ?? <></>}
            showDisabledTip={stagesProperties.length === 0}
            tipContent={t('navigation.noStagesAvailableTip')}
          />
        }
      />
    </header>
  )
}
